import StaticPage from '../_StaticPage'

// Revised September 2026 to cover marine weather and warnings, the nautical
// chart and depth layers, Go Mode, and the third-party data the product is
// built on. The per-source credits live on /data-sources so a new data source
// never requires a Terms revision. See
// docs/context/project_legal_pages_refresh_2026_09.md.
export default function Terms() {
  return (
    <StaticPage
      title="Terms of Service"
      description="Terms of Service for Tides Near Me - the tide, tidal current, marine weather and nautical chart app and website."
    >
      <p>
        <b>Last updated:</b> September 2026
      </p>
      <p>
        Please read these Terms of Service ("Terms") carefully before using the Tides Near Me mobile
        applications and website (collectively, the "Service"), operated by Shelter Island Mapping
        Company, LLC ("us", "we", or "our").
      </p>
      <p>
        Your access to and use of the Service is conditioned upon your acceptance of and compliance
        with these Terms. These Terms apply to all visitors, users, and others who access or use the
        Service.
      </p>
      <p>
        By accessing or using the Service, you agree to be bound by these Terms. If you do not agree
        to any part of these Terms, you do not have permission to use the Service.
      </p>
      <hr />
      <h2>Relationship to Platform Terms</h2>
      <p>
        If you access the Service through a mobile application, your use is also subject to the
        applicable platform terms, including:
      </p>
      <ul>
        <li>Apple Media Services Terms and Conditions (iOS)</li>
        <li>Google Play Terms of Service (Android)</li>
      </ul>
      <p>
        In the event of a conflict between these Terms and the platform provider's terms regarding
        billing, subscriptions, or payments, the platform provider's terms will control.
      </p>
      <p>These Terms are intended to supplement, and not replace, the applicable platform terms.</p>
      <hr />
      <h2>Description of the Service</h2>
      <p>
        Tides Near Me provides informational content about coastal waters based on available data
        sources, which may include:
      </p>
      <ul>
        <li>Tide predictions and tidal current predictions</li>
        <li>
          Marine weather forecasts, weather radar, and reproductions of official marine warnings
        </li>
        <li>
          Map layers showing charted depths, soundings, navigation marks, and other chart features
        </li>
        <li>
          A live position display ("Go Mode") that shows your device's reported position, heading,
          and speed on the map together with data from the layers above
        </li>
        <li>Educational guides and related informational content</li>
      </ul>
      <p>
        The Service may include free features and optional premium features. Feature availability
        may vary by platform, device, location, or data coverage.
      </p>
      <p>
        The website provides informational access only and does <b>not</b> offer subscriptions or
        process payments.
      </p>
      <hr />
      <h2>Subscriptions and Payments (Mobile Applications Only)</h2>
      <p>
        Optional auto-renewable subscriptions are available through the mobile applications only.
      </p>
      <ul>
        <li>Subscriptions are processed by the Apple App Store or Google Play Store</li>
        <li>Payment is charged to your Apple ID or Google account</li>
        <li>We do not collect, process, or store payment or billing information</li>
        <li>
          Subscription management, renewals, and cancellations must be handled through your platform
          account settings
        </li>
      </ul>
      <p>
        Subscriptions are <b>not available through the website</b>.
      </p>
      <hr />
      <h2>Free and Premium Features</h2>
      <p>
        Certain features of the Service are available at no cost, while others require a paid
        subscription.
      </p>
      <p>
        We reserve the right to modify, add, suspend, or discontinue features or functionality at
        any time without notice.
      </p>
      <hr />
      <h2>Advertising</h2>
      <p>
        The Service may display advertisements in free versions of the mobile applications and on
        the website. Advertising content is provided by third-party advertising partners and is
        subject to their terms and policies.
      </p>
      <hr />
      <h2>Third-Party Data</h2>
      <p>
        Much of the information in the Service is derived from data published by government
        agencies, hydrographic offices, meteorological services, and open-data communities. That
        data remains the property of its providers and is used under their respective licences. A
        list of providers and licences is maintained on our <a href="/data-sources">Data Sources</a>{' '}
        page, and specific credits appear within the Service, for example in the map's attribution
        control and alongside forecasts.
      </p>
      <p>
        The data providers are not affiliated with the Service, do not endorse it, and make no
        warranty about how their data is presented here. You may not remove or obscure any
        attribution or licence notice displayed in the Service.
      </p>
      <hr />
      <h2>Intellectual Property and Permitted Use</h2>
      <p>
        The Service and its original content, features, and functionality are and will remain the
        exclusive property of Shelter Island Mapping Company, LLC and its licensors.
      </p>
      <p>
        The Service is protected by copyright, trademark, and other laws of the United States and
        foreign countries. Our trademarks and trade dress may not be used without prior written
        consent.
      </p>
      <p>
        The Service is provided for your personal use through the mobile applications and the
        website. Except as permitted by a data provider's own licence, you may not use automated
        means to extract data from the Service's application programming interfaces, data feeds, or
        map layers in order to build, train, or operate another product or dataset, and you may not
        redistribute or resell that data or circumvent any access control that protects it. Public
        pages of the website may be indexed by search engines and similar crawlers.
      </p>
      <hr />
      <h2>Accuracy and Informational Use Only</h2>
      <p>The Service is provided for informational purposes only.</p>
      <p>
        All predictions, forecasts, depths, and other data in the Service are based on available
        sources and models and may be affected by environmental conditions, data limitations, or
        technical factors. We make no guarantees regarding accuracy, completeness, timeliness, or
        suitability for any particular purpose.
      </p>
      <p>
        <b>
          THE SERVICE IS NOT INTENDED FOR NAVIGATION, ROUTE PLANNING, VESSEL OPERATION,
          SAFETY-CRITICAL DECISIONS, OR EMERGENCY USE.
        </b>{' '}
        It should not be used as a substitute for official nautical charts, tide tables, official
        weather forecasts and warnings, navigational instruments, local notices to mariners, or
        professional judgment. The following applies in particular:
      </p>
      <p>
        <b>Tide and current predictions.</b> Predictions are astronomical. Actual water levels and
        current speeds are affected by weather, river flow, and other conditions and can differ
        substantially from the predictions shown.
      </p>
      <p>
        <b>Charts and depths.</b> Depth and chart layers are compiled from third-party survey data
        and other sources. They are not official nautical charts, are not corrected for notices to
        mariners, do not satisfy any chart-carriage requirement, and may be inaccurate, incomplete,
        or out of date. Depths are shown relative to differing vertical datums, which are not
        directly comparable. Depths and charted features are not verified and must not be used to
        determine safe water or a safe route.
      </p>
      <p>
        <b>Go Mode and live position.</b> Position, heading, speed, and the depth or conditions
        reported at your position are derived from your device's sensors and from the data layers
        described above. Any of them may be inaccurate, delayed, or unavailable. Go Mode is a
        reference display, not a navigation instrument or an aid to navigation. When operating a
        vessel you remain solely responsible for maintaining a proper lookout and for using official
        charts, instruments, and seamanship.
      </p>
      <p>
        <b>Marine weather, radar, and warnings.</b> Forecasts are generated from weather and wave
        models and may differ from the forecasts issued by the responsible national authority.
        Official marine warnings are reproduced without alteration where a compatible feed exists,
        but they may be delayed, incomplete, or absent, and many regions have no warning coverage in
        the Service at all. Radar imagery is observational and any short-term extrapolation is not a
        forecast. Always consult the responsible national weather service directly before going on
        the water.
      </p>
      <p>
        You assume full responsibility for any decisions made based on information provided by the
        Service.
      </p>
      <hr />
      <h2>Links to Other Websites</h2>
      <p>
        Our Service may contain links to third-party websites or services that are not owned or
        controlled by Shelter Island Mapping Company, LLC.
      </p>
      <p>
        We assume no responsibility for the content, privacy policies, or practices of any
        third-party websites or services.
      </p>
      <hr />
      <h2>Indemnification</h2>
      <p>
        You agree to defend, indemnify, and hold harmless Shelter Island Mapping Company, LLC and
        its officers, directors, employees, contractors, agents, licensors, and affiliates from and
        against any claims, damages, obligations, losses, liabilities, costs, or expenses (including
        reasonable attorneys' fees) arising out of or related to:
      </p>
      <p>
        a) your use of the Service, or
        <br />
        b) your violation of these Terms.
      </p>
      <hr />
      <h2>Limitation of Liability</h2>
      <p>
        TO THE MAXIMUM EXTENT PERMITTED BY LAW, SHELTER ISLAND MAPPING COMPANY, LLC AND ITS DATA
        PROVIDERS SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR
        PUNITIVE DAMAGES, OR FOR ANY LOSS, INJURY, OR DAMAGE ARISING FROM RELIANCE ON THE SERVICE,
        INCLUDING WHILE NAVIGATING OR OPERATING A VESSEL.
      </p>
      <hr />
      <h2>Disclaimer</h2>
      <p>Your use of the Service is at your sole risk.</p>
      <p>
        THE SERVICE IS PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS, WITHOUT WARRANTIES OF ANY
        KIND, WHETHER EXPRESS OR IMPLIED, INCLUDING ANY WARRANTY OF ACCURACY, FITNESS FOR A
        PARTICULAR PURPOSE, OR NON-INFRINGEMENT.
      </p>
      <hr />
      <h2>Governing Law</h2>
      <p>
        These Terms shall be governed and construed in accordance with the laws of the State of New
        York, United States, without regard to its conflict of law provisions.
      </p>
      <hr />
      <h2>Dispute Resolution and Arbitration</h2>
      <p>
        <b>Please read this section carefully. It affects your legal rights.</b>
      </p>
      <p>
        <b>Informal resolution first.</b> If you have a dispute with us, contact us at
        support@tidesnear.me with a description of the problem. We will try to resolve it
        informally. If we have not resolved it within 60 days of your notice, either of us may start
        arbitration as described below.
      </p>
      <p>
        <b>Binding arbitration.</b> Any dispute, claim, or controversy arising out of or relating to
        these Terms or the Service that is not resolved informally will be settled by binding
        individual arbitration administered by the American Arbitration Association under its
        Consumer Arbitration Rules, rather than in court. The arbitrator's decision is final and may
        be entered as a judgment in any court of competent jurisdiction. The Federal Arbitration Act
        governs the interpretation and enforcement of this section. Arbitration will take place in
        New York, or by telephone or video, or at another location we both agree to.
      </p>
      <p>
        <b>Exceptions.</b> Either of us may bring an individual claim in small claims court instead
        of arbitrating. Either of us may seek an injunction or other equitable relief in court to
        protect intellectual property rights.
      </p>
      <p>
        <b>Class action waiver.</b> YOU AND WE AGREE THAT EACH MAY BRING CLAIMS AGAINST THE OTHER
        ONLY IN AN INDIVIDUAL CAPACITY AND NOT AS A PLAINTIFF OR CLASS MEMBER IN ANY PURPORTED
        CLASS, CONSOLIDATED, OR REPRESENTATIVE PROCEEDING. The arbitrator may not consolidate more
        than one person's claims or preside over any form of class or representative proceeding.
      </p>
      <p>
        <b>30-day opt-out.</b> You may opt out of this arbitration agreement by emailing
        support@tidesnear.me within 30 days of first accepting these Terms, stating that you opt out
        of arbitration. Opting out does not affect any other part of these Terms.
      </p>
      <p>
        <b>Where you live.</b> If you are a consumer in a jurisdiction whose law does not permit
        mandatory arbitration or a class action waiver, this section applies only to the extent that
        law allows, and nothing in these Terms limits any rights you have under it.
      </p>
      <hr />
      <h2>Changes to These Terms</h2>
      <p>
        We reserve the right, at our sole discretion, to modify or replace these Terms at any time.
        Continued use of the Service after changes become effective constitutes acceptance of the
        revised Terms.
      </p>
      <hr />
      <h2>Contact Us</h2>
      <p>If you have any questions about these Terms, please contact us at:</p>
      <ul>
        <li>
          <b>Email:</b> <a href="mailto:support@tidesnear.me">support@tidesnear.me</a>
        </li>
      </ul>
    </StaticPage>
  )
}
